/**
 * Project-wide z-index registry. Prefer these constants over ad-hoc `z-[NN]` literals.
 * Marketing / SaaS tiers are documented here so overlays (e.g. consent at 400+) stay coherent.
 *
 * Cross-reference design tokens: `tooling/tailwind/theme.css` (comment block links here).
 *
 * SaaS chrome stack (typical):
 * - completion host underlay / learner preloader ≈
 *   {@link Z_INDEX.saasLearnerPreloader} 118
 * - completion overlay shell ≈ 120
 * - {@link Z_INDEX.saasRightSidebarPanel} 122 (above preloader; SnapEdgeHandle inherits)
 * - left nav / mobile nav ≈ 120–125 (SnapEdgeHandle inherits — must stay above preloader)
 * - HUD drawers / under-header sheets ≈ 125 (`saasAppUnderHeaderSurfaceZ`)
 * - header ≈ 130
 * - scene/battle **stage** must `isolate` + `z-0` so in-stage rails (210) /
 *   SideDrawer (200) cannot outrank body-portaled HUD drawers at 125
 * - dialogs / sheets / menus ≈ {@link Z_INDEX.saasDropdown} 200
 * - BattleHudPanelOverlay (brief / decision / response) ≈ 300 — Host gates these
 *   while the learner preloader is visible (do not raise preloader above chrome edges)
 */
export const Z_INDEX = {
	marketingBackdrop: 1,
	marketingHexInteractables: 20,
	marketingChrome: 100,
	marketingCarouselOverlay: 120,
	saasRightSidebarPanel: 122,
	saasMobileNav: 120,
	saasSidebar: 125,
	saasHeaderBar: 130,
	saasDropdown: 200,
	saasWorkspaceSyncBanner: 209,
	saasConnectivityBanner: 210,
	saasOverlayMax: 260,
	/**
	 * Quiz answer feedback VFX (tick/cross + confetti / optional PrismaticBurst).
	 * Full-viewport portal — above SaaS chrome (≤260), below consent (400).
	 * Keep in sync with `QUIZ_FEEDBACK_VFX_Z_INDEX` in `@repo/ui` quiz feedback layer.
	 */
	saasQuizAnswerFeedback: 300,
	/**
	 * Battle / scenario / IC learner preloader (host-local absolute).
	 * Below right sidebar (122) / left nav (125) so {@link SnapEdgeHandle} on
	 * those edges stays draggable. CONVERSATION brief / decision / response
	 * (HUD ≈ 300) are gated by the encounter Host until dismiss — do not raise
	 * this above chrome edges to cover those panels.
	 */
	saasLearnerPreloader: 118,
	/**
	 * Prefer-over ContentTooltip portals. Above quiz VFX (300) / SaaS overlay max (260);
	 * below consent (400). Keep in sync with `CONTENT_TOOLTIP_Z_INDEX` in `@repo/ui`.
	 */
	saasContentTooltip: 320,
	/**
	 * Full-viewport marketing “rotate to landscape” hint (portaled above yarl’s
	 * `.yarl__portal`, default / var `--yarl__portal_zindex` 9999). Stay below
	 * the smooth-cursor fixed layer (100_000) so cursor stacking stays coherent.
	 */
	marketingPortraitRotateHintFixed: 99_999,
	consent: {
		backdrop: 400,
		content: 401,
		dialog: 401,
	},
} as const;
